import { Component, OnInit, Output, EventEmitter, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';

// fontAwsome
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'

// Services
import { ChatSupportService } from 'src/app/_services/chat-support.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export type ChatSupportMessage = { type?: string, subtype?: string, id?: number, url?: string, title?: string, link: string };

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'chat-support',
  templateUrl: './chat-support.component.html',
  styleUrls: ['./chat-support.component.scss']
})
export class ChatSupportComponent implements OnInit, AfterViewChecked {
  // FontAwsome 
  faExternalLink = faExternalLinkAlt;
  chatOpen: boolean = false;
  chatTitle: string = 'inventoryahead';
  pages: object = {
    faq: false,
    getInTouch: false,
    chat: true,
    leaveMessage: false,
    messageSentSuccess: false
  };
  faq: any;
  messageSendHtml: string = 'Send';
  navHistory: string[] = ['chat'];
  question: string = '';
  message: string = '';
  messages: Partial<ChatSupportMessage>[][] = [];
  isSameSender: boolean = true;
  scrollPxls: number = 0;
  subject: string = '';
  errors: any = {
    subject: '',
    message: ''
  };
  timeOut: any;

  @ViewChild("messageList") messageList: ElementRef;
  @ViewChild("messageArea") messageArea: ElementRef;
  @ViewChild("chatSupport") chatSupport: ElementRef;
  @ViewChild("avatar") avatar: ElementRef;

  @Output() chatButtonClicked = new EventEmitter();

  constructor(public chatService: ChatSupportService,
  ) { }

  ngOnInit() {
    window.setTimeout(() => {
      this.messages.push([{ title: 'Hi! Welcome to inventoryahead!', type: 'bot_message' }]);
      window.setTimeout(() => {
        this.messages[0].push({ title: "Ask me a question and I'll find the answer for you.", type: 'bot_message' });
      }, 1500);
    }, 2600);
  }

  ngAfterViewChecked(): void {
    if (this.messageList) {
      this.messageList.nativeElement.scrollTo({ left: 0, top: this.messageList.nativeElement.scrollHeight, behavior: 'auto' });
    }
  }

  isSubsequentSender(index1: number, index2: number) {
    if (this.messages[index1].length > 1 && this.messages[index1][index2 - 1]) {
      return this.messages[index1][index2].type == this.messages[index1][index2 - 1].type;
    }
    return false;
  }

  sendQuestion() {
    if (!this.question) {
      return;
    }

    this.messages.push([{ title: this.messageArea.nativeElement.value, type: 'user_message' }]);

    window.setTimeout(() => {
      this.chatService.botIsTyping = true;
    }, 400);

    this.question = '';
    this.chatService.searchFaq(this.messageArea.nativeElement.value)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
          window.setTimeout(() => {
            if (Object.keys(result).length) {
              this.messages.push([{ title: 'Here are some results that we found:', type: 'bot_message' }]);
              window.setTimeout(() => {
                let res: any = result;
                this.messages[this.messages.length - 1].push(...res);
              }, 800);
            } else {
              this.messages.push([{ title: "I couldn't find any relevant articles.", type: 'bot_message' }]);
              window.setTimeout(() => {
                this.messages[this.messages.length - 1].push({ title: "You can ask another question.", type: 'bot_message' });
                window.setTimeout(() => {
                  this.messages[this.messages.length - 1].push({ title: "Or you can get in touch.", type: 'bot_message' });
                }, 1500);
              }, 1500);
            }
            this.chatService.botIsTyping = false;

          }, 1000);
    });
  }

  sendMessage(event) {
    event.preventDefault();
    if (!this.checkErrors()) {
      this.messageSendHtml = '<i class="fa fa-spinner fa-pulse"></i>';

      const payload =  {
        customerName: `${this.chatService.UserState.firstName} ${this.chatService.UserState.lastName}`,
        customerMail: this.chatService.UserState.email,
        customerQuestion: this.message,
        submittedFrom: window.location.href,
        subject: this.subject
      };
      this.chatService.sendMessage(payload)
        .pipe(untilDestroyed(this))
        .subscribe(_ => {
          this.message = '';
          this.subject = '';
          this.messageSendHtml = 'Send';
          this.navigateTo('messageSentSuccess');
      });
    }
  }

  openChatSupport() {
    this.chatOpen = true;
  }

  closeChatSupport() {
    this.chatOpen = false;
  }

  clickedEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.sendQuestion();
    }
  }

  loadFaq(id: number) {
    this.chatService.botIsTyping = true;
    this.chatService.getFaq(id)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.faq = result;
        window.setTimeout(() => {
          this.chatService.botIsTyping = false;
          this.navigateTo('faq');
      }, 1000);
    });
  }

  naviGatetoFaq(url: string) {
    window.location.href = url;
  }

  navigateTo(page: string) {
    for (const key of Object.keys(this.pages)) {
      if (page === key) {
        this.pages[key] = true;
      } else {
        this.pages[key] = false;
      }
    }
    this.navHistory.push(page);
  }

  goBack() {
    if (this.navHistory.length > 1) {
      this.navigateTo(this.navHistory[this.navHistory.length - 2]);
    }
  }

  getInTouch() {
    this.messages.push([{ title: 'Get in Touch', type: 'user_message' }]);
    window.setTimeout(() => {
      this.chatService.botIsTyping = true;
      window.setTimeout(() => {
        this.messages.push([{ title: 'How do you like to get in touch?', link: 'Leave a message', url: '', type: 'get_in_touch' }]);
        this.chatService.botIsTyping = false;
      }, 800);
    }, 200);
  }

  get GetInTouchPage() {
    return this.pages['getInTouch'];
  }

  checkErrors() {
    if (!this.subject) {
      this.errors.subject = 'The subject is required'
    } else {
      this.errors.subject = '';
    }
    if (!this.message) {
      this.errors.message = 'Please describe your issue'
    } else {
      this.errors.message = '';
    }
    return this.errors.message != '' || this.errors.subject != '';
  }

  checkError(field) {
    if (!this[field]) {
      this.errors[field] = 'Please fill out this field'
    } else {
      this.errors[field] = '';
    }
    return this.errors[field] != '';
  }

  parseSymbols(content: string) {
    return content
      .replace('&#038;', '&')
      .replace('&#8220;', '“')
      .replace('&#8221', '”');
  }
}


